// src/services/document.service.js
import { api } from './api';

// UUID generation utility
const generateUUID = () => {
    // Use browser's crypto API to generate RFC4122 compliant UUID
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

class DocumentService {
    // Generate a new document from template
    async generateDocument(documentData) {
        try {
            const response = await api.post('/api/documents/generate', documentData);
            return response.data;
        } catch (error) {
            console.error('Error generating document:', error);
            throw error;
        }
    }

    // Save document as draft
    async saveDraft(draftData) {
        try {
            const response = await api.post('/api/documents/drafts', draftData);
            return response.data;
        } catch (error) {
            console.error('Error saving draft:', error);
            // Fallback to localStorage with UUID
            const drafts = JSON.parse(localStorage.getItem('draftDocuments') || '[]');
            const updatedDrafts = [...drafts, { ...draftData, id: draftData.id || generateUUID() }];
            localStorage.setItem('draftDocuments', JSON.stringify(updatedDrafts));
            return draftData;
        }
    }

    // Get all draft documents
    async getDrafts() {
        try {
            const response = await api.get('/api/documents/drafts');
            return response.data;
        } catch (error) {
            console.error('Error fetching drafts:', error);
            // Fallback to localStorage
            return JSON.parse(localStorage.getItem('draftDocuments') || '[]');
        }
    }

    // Update draft document
    async updateDraft(draftId, draftData) {
        try {
            const response = await api.put(`/api/documents/drafts/${draftId}`, draftData);
            return response.data;
        } catch (error) {
            console.error('Error updating draft:', error);
            // Fallback to localStorage
            const drafts = JSON.parse(localStorage.getItem('draftDocuments') || '[]');
            const updatedDrafts = drafts.map(d => d.id === draftId ? { ...d, ...draftData } : d);
            localStorage.setItem('draftDocuments', JSON.stringify(updatedDrafts));
            return draftData;
        }
    }

    // Delete draft document
    async deleteDraft(draftId) {
        try {
            await api.delete(`/api/documents/drafts/${draftId}`);
            return true;
        } catch (error) {
            console.error('Error deleting draft:', error);
            // Fallback to localStorage
            const drafts = JSON.parse(localStorage.getItem('draftDocuments') || '[]');
            const updatedDrafts = drafts.filter(d => d.id !== draftId);
            localStorage.setItem('draftDocuments', JSON.stringify(updatedDrafts));
            return true;
        }
    }

    // Finalize document (save to backend database)
    async finalizeDocument(documentData) {
        try {
            console.log('📤 Saving document to backend:', documentData.id);
            const response = await api.post('/api/documents/', documentData);

            if (response.data && response.data.success !== false) {
                console.log('✅ Document saved to backend successfully');
                return response.data;
            } else {
                throw new Error(response.data?.error || 'Failed to save document');
            }
        } catch (error) {
            console.error('❌ Error saving document to backend:', error);

            // Handle different error types
            if (error.response?.status === 401 || error.response?.status === 403) {
                throw new Error('Authentication required. Please log in again.');
            } else if (error.response?.status >= 500) {
                throw new Error(`Server error: ${error.response.data?.error || error.message}`);
            } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
                throw new Error('Unable to connect to server. Please check your connection.');
            } else {
                throw new Error(`Failed to save document: ${error.response?.data?.error || error.message}`);
            }
        }
    }

    // Get my documents (documents I created/uploaded)
    async getMyDocuments() {
        try {
            const response = await api.get('/api/documents/my-documents');
            return response.data;
        } catch (error) {
            console.error('Error fetching my documents:', error);
            return { success: false, documents: [] };
        }
    }

    // Get all generated documents
    async getGeneratedDocuments() {
        try {
            const response = await api.get('/api/documents/generated');
            return response.data;
        } catch (error) {
            console.error('Error fetching generated documents:', error);
            // Return empty array on error - no localStorage fallback
            return { success: false, documents: [] };
        }
    }

    // Send document via email
    async sendDocumentEmail(documentId, employeeId, emailData) {
        try {
            const response = await api.post(`/api/documents/${documentId}/send`, {
                employeeIds: [employeeId],
                deliveryMethod: 'email',
                emailData: {
                    to: emailData.to,
                    subject: emailData.subject,
                    documentTitle: emailData.documentTitle,
                    recipientName: emailData.recipientName
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error sending document via email:', error);
            throw error;
        }
    }

    // Send document to Oak HR (employee portal)
    async sendToOakHR(documentId, employeeId) {
        try {
            console.log(`📤 Sending document ${documentId} to employee ${employeeId} via API...`);

            // Validate inputs before making the API call
            if (!documentId || !employeeId) {
                throw new Error('Document ID and Employee ID are required for document delivery');
            }

            const response = await api.post(`/api/documents/${documentId}/send`, {
                employeeIds: [employeeId],
                deliveryMethod: 'portal'
            });

            console.log('✅ Document successfully sent via backend API');
            return response.data;

        } catch (error) {
            console.error('❌ Error sending document to Oak HR:', error.message);

            // Handle different error types appropriately
            if (error.response?.status === 404) {
                throw new Error('Document not found. Please ensure the document exists in the system.');
            } else if (error.response?.status >= 500) {
                const errorMessage = error.response.data?.error || error.message;
                throw new Error(`Server error: ${errorMessage}. Please try again later.`);
            } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
                throw new Error('Unable to connect to server. Please check your internet connection.');
            } else {
                throw new Error(`Failed to send document: ${error.response?.data?.error || error.message}`);
            }
        }
    }

    // Get employee documents (for employee portal)
    async getEmployeeDocuments(employeeId) {
        try {
            console.log(`📡 Fetching documents for employee ${employeeId} from API...`);
            const response = await api.get(`/api/documents/employee/${employeeId}`);

            if (response.data && response.data.success !== false) {
                const apiDocs = response.data.documents || response.data;
                console.log(`✅ API returned ${apiDocs?.length || 0} documents`);

                return {
                    success: true,
                    documents: apiDocs,
                    source: 'api'
                };
            } else {
                throw new Error(response.data?.error || 'API returned false success');
            }

        } catch (error) {
            console.error('❌ Error fetching employee documents:', error.message);

            // Handle different error types appropriately
            if (error.response?.status === 404) {
                throw new Error('No documents found for this employee.');
            } else if (error.response?.status >= 500) {
                const errorMessage = error.response.data?.error || error.message;
                throw new Error(`Server error: ${errorMessage}. Please try again later.`);
            } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
                throw new Error('Unable to connect to server. Please check your internet connection.');
            } else {
                throw new Error(`Failed to fetch documents: ${error.response?.data?.error || error.message}`);
            }
        }
    }

    // Save employee document locally (for testing)
    saveEmployeeDocumentLocally(employeeId, document) {
        const key = `employeeDocuments_${employeeId}`;
        const existingDocs = JSON.parse(localStorage.getItem(key) || '[]');
        const updatedDocs = [...existingDocs, { ...document, receivedAt: new Date().toISOString() }];
        localStorage.setItem(key, JSON.stringify(updatedDocs));
        return updatedDocs;
    }

    // Download document
    async downloadDocument(documentId) {
        try {
            const response = await api.get(`/api/documents/${documentId}/download`, {
                responseType: 'blob'
            });

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `document_${documentId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            console.error('Error downloading document:', error);
            throw error;
        }
    }

    // Get document preview
    async getDocumentPreview(documentId) {
        try {
            const response = await api.get(`/api/documents/${documentId}/preview`);
            return response.data;
        } catch (error) {
            console.error('Error fetching document preview:', error);
            throw error;
        }
    }

    // Batch send documents
    async batchSendDocuments(documents, sendOptions) {
        try {
            const response = await api.post('/api/documents/batch-send', {
                documents,
                options: sendOptions
            });
            return response.data;
        } catch (error) {
            console.error('Error batch sending documents:', error);
            throw error;
        }
    }

    // Get document send status
    async getDocumentStatus(documentId) {
        try {
            const response = await api.get(`/api/documents/${documentId}/status`);
            return response.data;
        } catch (error) {
            console.error('Error fetching document status:', error);
            return { status: 'unknown' };
        }
    }

    // Search documents
    async searchDocuments(query, filters = {}) {
        try {
            const response = await api.get('/api/documents/search', {
                params: { q: query, ...filters }
            });
            return response.data;
        } catch (error) {
            console.error('Error searching documents:', error);
            return [];
        }
    }

    // Get document statistics
    async getDocumentStats() {
        try {
            const response = await api.get('/api/documents/stats');
            return response.data;
        } catch (error) {
            console.error('Error fetching document stats:', error);
            return {
                total: 0,
                drafts: 0,
                generated: 0,
                sent: 0
            };
        }
    }
}

const documentService = new DocumentService();
export default documentService;